import CustomButton from 'components/buttons/CustomButton'
import ORFIDropzone from 'components/files/Dropzone'
import RPGDInfo from 'components/files/RPGDInfo'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import Spinner from 'react-bootstrap/Spinner'
import FontAwesome from 'react-fontawesome'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import I18n from 'resources/i18n/I18n'
import { fetchComplaintSuccess } from 'store/actions/resourceActions'
import { errorToast, successToast } from 'utils/toast'
import ComplaintApi from '../../services/complaintApi'
import EventApi from '../../services/eventApi'
import CONSTANTS from '../../utils/constants'
import Loader from '../loader/Loader'
import Logo from '../logo/Logo'
import ValidationConfirmModal from '../modal/ValidationConfirmModal'
import ActionsGrid from '../status/ActionsGrid'
import CommentChat from '../status/CommentChat'
import PrejudiceFields from '../status/PrejudiceFields'
import Status from '../status/Status'
import Title from '../typo/Title'

const IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/jpg']

const createPendingStatus = (eventId, lastProcessing) => ({
  id: -1,
  status: CONSTANTS.STATUS_LIST.PROCESSING,
  date: null,
  comment: null,
  itt: lastProcessing?.itt || 0,
  prejudice: lastProcessing?.prejudice || 0,
  arretTravail: lastProcessing?.arretTravail || 0,
  autrePrejudice: '',
  autreAction: '',
  idUser: null,
  idEvent: eventId,
  complaints: [],
})

const deduplicateComplaints = (complaints) =>
  [...complaints]
    .reverse()
    .filter((c, i, arr) => arr.findIndex((x) => x.id === c.id) === i)
    .reverse()

const Lifecycle = () => {
  const [event, setEvent] = useState(null)
  const [files, setFiles] = useState([])
  const [isFormDisabled, setIsFormDisabled] = useState(false)
  const [loading, setLoading] = useState(false)
  const [newComment, setNewComment] = useState('')
  const [showValidateModal, setShowValidateModal] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [newComplaintIds, setNewComplaintIds] = useState([])
  const [pendingStatus, setPendingStatus] = useState(null)

  const dispatch = useDispatch()
  const history = useHistory()
  const { id } = useParams()
  const resources = useSelector((state) => state.resourceReducer)
  const currentUser = useSelector((state) => state.userReducer.user)

  const canReject = useMemo(() => {
    if (!currentUser?.roles) return false
    const rejectRoles = [CONSTANTS.ROLES.MANAGER, CONSTANTS.ROLES.DIRECTOR, CONSTANTS.ROLES.ADMIN]
    return currentUser.roles.some((r) => rejectRoles.includes(r))
  }, [currentUser])

  useEffect(() => {
    setLoading(true)
    EventApi.getEvent(id)
      .then((response) => {
        response.statuses.sort((a, b) => new Date(a.date) - new Date(b.date))
        setEvent(response)

        const isClosed = response.currentStatus === CONSTANTS.STATUS_LIST.REFUSED || response.currentStatus === CONSTANTS.STATUS_LIST.VALIDATED
        setIsFormDisabled(isClosed)

        if (!isClosed) {
          const lastProcessing = [...response.statuses].reverse().find((s) => s.status === CONSTANTS.STATUS_LIST.PROCESSING && s.id !== -1)
          setPendingStatus(createPendingStatus(response.id, lastProcessing))
        }
      })
      .finally(() => setLoading(false))

    if (resources?.complaints.length === 0) {
      ComplaintApi.getComplaintList().then((response) => dispatch(fetchComplaintSuccess(response)))
    }
  }, [id, resources?.complaints.length, dispatch])

  const processingStatuses = useMemo(() => {
    if (!event) return []
    return event.statuses.filter((s) => s.status === CONSTANTS.STATUS_LIST.PROCESSING && s.id !== -1)
  }, [event])

  const previousComplaintIds = useMemo(
    () => [...new Set(processingStatuses.flatMap((s) => (s.complaints || []).map((c) => c.id)))],
    [processingStatuses],
  )

  const allProcessingComplaints = useMemo(() => deduplicateComplaints(processingStatuses.flatMap((s) => s.complaints || [])), [processingStatuses])

  const previousAutreActions = useMemo(
    () =>
      processingStatuses
        .filter((s) => s.autreAction && s.autreAction.trim().length > 0)
        .map((s) => ({ text: s.autreAction.trim(), idUser: s.idUser, date: s.date })),
    [processingStatuses],
  )

  const previousAutrePrejudices = useMemo(
    () =>
      processingStatuses
        .filter((s) => s.autrePrejudice && s.autrePrejudice.trim().length > 0)
        .map((s) => ({ text: s.autrePrejudice.trim(), idUser: s.idUser, date: s.date })),
    [processingStatuses],
  )

  const lastProcessingPrejudice = useMemo(() => {
    const last = [...processingStatuses].reverse().find((s) => s.itt || s.prejudice || s.arretTravail)
    if (!last) return {}
    return { itt: last.itt, prejudice: last.prejudice, arretTravail: last.arretTravail }
  }, [processingStatuses])

  const selectedComplaintIds = useMemo(() => [...new Set([...previousComplaintIds, ...newComplaintIds])], [previousComplaintIds, newComplaintIds])

  const toggleComplaint = useCallback(
    (complaintId) => {
      if (previousComplaintIds.includes(complaintId)) return
      setNewComplaintIds((prev) => (prev.includes(complaintId) ? prev.filter((id) => id !== complaintId) : [...prev, complaintId]))
    },
    [previousComplaintIds],
  )

  const editPendingStatus = useCallback((updatedStatus) => {
    setPendingStatus(updatedStatus)
  }, [])

  const getFilesForStatus = useCallback((statusId) => (event ? event.files.filter((file) => file.statusId === statusId) : []), [event])

  const previousProcessingFiles = useMemo(
    () => processingStatuses.flatMap((s) => (event ? event.files.filter((file) => file.statusId === s.id) : [])),
    [processingStatuses, event],
  )

  const getFilesToAnonymize = () => {
    const newFiles = files.filter((file) => !file.id)
    return newFiles.filter((file) => IMAGE_MIME_TYPES.includes(file.type))
  }

  const hasNewContent = () => {
    const hasPrejudiceData =
      Number(pendingStatus?.itt) > 0 ||
      Number(pendingStatus?.prejudice) > 0 ||
      Number(pendingStatus?.arretTravail) > 0 ||
      (pendingStatus?.autrePrejudice && pendingStatus.autrePrejudice.trim().length > 0)
    const hasAutreAction = pendingStatus?.autreAction && pendingStatus.autreAction.trim().length > 0
    return (newComment && newComment.trim().length > 0) || newComplaintIds.length > 0 || hasPrejudiceData || hasAutreAction
  }

  const saveProcessing = async (filesToAnonymize = []) => {
    if (!pendingStatus) return null
    if (!hasNewContent() && files.length === 0) return null

    const statusToSave = {
      ...pendingStatus,
      comment: newComment || null,
      complaintList: newComplaintIds,
    }

    const res = await EventApi.createStatus(event.id, statusToSave)

    if (res?.statuses?.length > 0 && files.length > 0) {
      const lastStatusId = res.statuses[res.statuses.length - 1].id
      await EventApi.uploadFiles(event.id, files, lastStatusId, filesToAnonymize)
    }

    return res
  }

  const withLoadingAndRedirect = async (action) => {
    setLoading(true)
    try {
      await action()
      successToast(I18n.t('EditToastSuccess', { id: event.id }))
    } catch {
      errorToast(I18n.t('EditToastError', { id: event.id }))
    } finally {
      setLoading(false)
      history.push('/consulter')
    }
  }

  const onSaveDraft = () => withLoadingAndRedirect(() => saveProcessing(getFilesToAnonymize()))

  const onValidateConfirmed = () => {
    setShowValidateModal(false)
    return withLoadingAndRedirect(async () => {
      await saveProcessing(getFilesToAnonymize())
      await EventApi.createStatus(event.id, {
        id: -1,
        status: CONSTANTS.STATUS_LIST.VALIDATED,
        date: null,
        comment: null,
        idUser: null,
        idEvent: event.id,
        complaints: [],
      })
    })
  }

  const onRejectConfirmed = () => {
    setShowRejectModal(false)
    return withLoadingAndRedirect(() =>
      EventApi.createStatus(event.id, {
        id: -1,
        status: CONSTANTS.STATUS_LIST.REFUSED,
        date: null,
        comment: newComment || null,
        idUser: null,
        idEvent: event.id,
        complaints: [],
      }),
    )
  }

  return (
    <div className="container-fluid">
      <Logo />
      {!loading ? (
        <div className="container">
          <div className="row">
            <Title text={I18n.t('LifecycleTitle')} />
          </div>

          {/* Read-only display of non-PROCESSING statuses (NEW, VALIDATED, REFUSED) */}
          <div className="row">
            {event &&
              event.statuses
                .filter((s) => s.id !== -1 && s.status !== CONSTANTS.STATUS_LIST.PROCESSING)
                .map((status) => (
                  <Status
                    key={status.id}
                    status={status}
                    files={getFilesForStatus(status.id)}
                    isFormDisabled={isFormDisabled}
                    allProcessingComplaints={status.status === CONSTANTS.STATUS_LIST.VALIDATED ? allProcessingComplaints : undefined}
                    lastProcessingPrejudice={status.status === CONSTANTS.STATUS_LIST.VALIDATED ? lastProcessingPrejudice : undefined}
                    previousAutreActions={status.status === CONSTANTS.STATUS_LIST.VALIDATED ? previousAutreActions : undefined}
                    previousAutrePrejudices={status.status === CONSTANTS.STATUS_LIST.VALIDATED ? previousAutrePrejudices : undefined}
                  />
                ))}
          </div>

          {/* Unified editing section */}
          {event && !isFormDisabled && pendingStatus && (
            <div className="row">
              <div className="container card-status mb-3">
                <div className="label mb-2" style={{ backgroundColor: CONSTANTS.STATUS[1].color }}>
                  {CONSTANTS.STATUS[1].label}
                </div>

                {/* Actions */}
                <ActionsGrid
                  status={pendingStatus}
                  editStatus={editPendingStatus}
                  complaints={resources.complaints}
                  selectedComplaintIds={selectedComplaintIds}
                  previousComplaintIds={previousComplaintIds}
                  readOnly={false}
                  onToggleComplaint={toggleComplaint}
                />

                {/* Récapitulatif des actions sélectionnées */}
                {(allProcessingComplaints.length > 0 || previousAutreActions.length > 0) && (
                  <div className="actions-summary mb-3">
                    <span className="subtitle">{I18n.t('ActionsSummary')}</span>
                    <ul className="actions-summary-list">
                      {allProcessingComplaints.map((c) => (
                        <li key={c.id}>
                          {c.label}
                          {c.asso_complaint_status && (
                            <span className="action-traceability">
                              {' '}
                              — {c.asso_complaint_status.idUser}
                              {c.asso_complaint_status.actionDate && `, ${moment(c.asso_complaint_status.actionDate).format('DD/MM/YYYY HH:mm')}`}
                            </span>
                          )}
                        </li>
                      ))}
                      {previousAutreActions.map((a, idx) => (
                        <li key={`autre-${idx}`} style={{ whiteSpace: 'pre-line' }}>
                          {a.text}
                          <span className="action-traceability">
                            {' '}
                            — {a.idUser}
                            {a.date && `, ${moment(a.date).format('DD/MM/YYYY HH:mm')}`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Commentaires */}
                <CommentChat statuses={event.statuses} event={event} newComment={newComment} onCommentChange={setNewComment} readOnly={false} />

                {/* Préjudices */}
                <PrejudiceFields
                  status={pendingStatus}
                  editStatus={editPendingStatus}
                  readOnly={false}
                  previousAutrePrejudices={previousAutrePrejudices}
                />

                {/* Pièces jointes */}
                {currentUser?.group?.enabledAttachmentAndComment && (
                  <ORFIDropzone
                    className="mt-3"
                    files={[...previousProcessingFiles, ...files]}
                    setFiles={(updatedFiles) => setFiles(updatedFiles.filter((f) => !f.statusId))}
                    title={<span className="subtitle">{I18n.t('EventFiles')}</span>}
                    subtitle={<RPGDInfo />}
                    downloadFileOnServer={EventApi.downloadFile}
                    deleteFileOnServer={(fileId) =>
                      EventApi.deleteFile(fileId).then((res) => {
                        setEvent((prev) => ({ ...prev, files: prev.files.filter((f) => f.id !== fileId) }))
                        return res
                      })
                    }
                    disabled={false}
                    readOnly={false}
                  />
                )}
              </div>
            </div>
          )}

          {/* Read-only comment history when form is disabled */}
          {event && isFormDisabled && (
            <div className="row">
              <div className="container card-status mb-3">
                <CommentChat statuses={event.statuses} event={event} readOnly={true} />
                {currentUser?.group?.enabledAttachmentAndComment && previousProcessingFiles.length > 0 && (
                  <ORFIDropzone
                    className="mt-3"
                    files={previousProcessingFiles}
                    setFiles={() => {}}
                    title={<span className="subtitle">{I18n.t('EventFiles')}</span>}
                    subtitle={<RPGDInfo />}
                    downloadFileOnServer={EventApi.downloadFile}
                    deleteFileOnServer={EventApi.deleteFile}
                    disabled={true}
                    readOnly={true}
                  />
                )}
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="row justify-content-center p-4">
            <div className="p-1">
              <CustomButton onClick={() => history.push(`/signalement/${id}`)} icon={<FontAwesome name="arrow-left" />} small secondary />
            </div>
            {!isFormDisabled && (
              <>
                <div className="p-1">
                  <CustomButton onClick={onSaveDraft} title={I18n.t('CommonSave')} small primary />
                </div>
                <div className="p-1">
                  <CustomButton onClick={() => setShowValidateModal(true)} title={I18n.t('ValidateDefinitely')} small success />
                </div>
                {canReject && (
                  <div className="p-1">
                    <CustomButton onClick={() => setShowRejectModal(true)} title={I18n.t('RejectFI')} small danger />
                  </div>
                )}
              </>
            )}
          </div>

          <ValidationConfirmModal
            showModal={showValidateModal}
            onHideModal={() => setShowValidateModal(false)}
            onValidateModal={onValidateConfirmed}
            loading={loading}
          />

          <Modal className="modal" show={showRejectModal} onHide={() => setShowRejectModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>{I18n.t('RejectConfirmTitle')}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="d-flex justify-content-center align-items-center">
              {loading ? (
                <Spinner animation="border" variant="primary" />
              ) : (
                <div className="d-flex flex-column align-items-center gap-y-4">
                  <p>{I18n.t('RejectConfirmMessage')}</p>
                  <div className="d-flex align-items-center justify-content-center gap-x-2">
                    <CustomButton onClick={onRejectConfirmed} small danger title={I18n.t('Yes')} />
                    <CustomButton onClick={() => setShowRejectModal(false)} small secondary title={I18n.t('No')} />
                  </div>
                </div>
              )}
            </Modal.Body>
          </Modal>
        </div>
      ) : (
        <Loader displayMessage />
      )}
    </div>
  )
}

export default Lifecycle
